<!--
/**
 * APCWO customisation for OrangeHRM — Personal Review module.
 *
 * Licensed under the GNU General Public License v2.0 or later, the same terms
 * as OrangeHRM itself. See the LICENSE file at the project root.
 */
-->

<template>
  <oxd-dialog
    class="orangehrm-dialog-modal apcwo-pr-root apcwo-pr-modal"
    @update:show="onClose"
  >
    <div class="orangehrm-modal-header">
      <oxd-text type="card-title">
        {{ employeeName }}
      </oxd-text>
    </div>
    <oxd-divider />

    <div v-if="isLoading" class="apcwo-pr-modal__muted">Loading…</div>
    <div v-else-if="review" class="apcwo-pr-modal__body">
      <div class="apcwo-pr-modal__facts">
        <div>
          <span class="apcwo-pr-facts__label">Cycle</span>
          <span class="apcwo-pr-facts__value">{{ review.cycle.name }}</span>
        </div>
        <div>
          <span class="apcwo-pr-facts__label">Status</span>
          <status-badge :status="review.status" />
        </div>
        <div>
          <span class="apcwo-pr-facts__label">Team</span>
          <span class="apcwo-pr-facts__value">{{ review.team || '—' }}</span>
        </div>
        <div>
          <span class="apcwo-pr-facts__label">Role</span>
          <span class="apcwo-pr-facts__value">
            {{ review.roleTitle || '—' }}
          </span>
        </div>
        <div>
          <span class="apcwo-pr-facts__label">Manager</span>
          <span class="apcwo-pr-facts__value">
            {{ review.managerName || '—' }}
          </span>
        </div>
        <div>
          <span class="apcwo-pr-facts__label">Submitted</span>
          <span class="apcwo-pr-facts__value">
            {{ review.submittedAt || '—' }}
          </span>
        </div>
      </div>

      <template v-if="review.employeeData">
        <h4 class="apcwo-pr-modal__title">Employee submission</h4>
        <h4 class="apcwo-pr-modal__heading">{{ schema.lifeVision.title }}</h4>
        <p class="apcwo-pr-modal__label">Personal life vision</p>
        <p class="apcwo-pr-modal__text">{{ emp.lifeVision || '—' }}</p>
        <p class="apcwo-pr-modal__label">{{ schema.apcHelpLabel }}</p>
        <p class="apcwo-pr-modal__text">{{ emp.apcHelp || '—' }}</p>

        <template v-for="g in goalBlocks" :key="g.key">
          <h4 class="apcwo-pr-modal__heading">{{ g.title }}</h4>
          <ol class="apcwo-pr-modal__goals">
            <li v-for="(row, i) in g.rows" :key="i">
              <strong>{{ row.objective || '—' }}</strong>
              <div>{{ row.action || '—' }}</div>
            </li>
          </ol>
        </template>

        <template v-for="(sec, key) in schema.textSections" :key="key">
          <h4 class="apcwo-pr-modal__heading">{{ sec.title }}</h4>
          <p class="apcwo-pr-modal__text">{{ emp[key] || '—' }}</p>
        </template>

        <h4 class="apcwo-pr-modal__heading">Employee evaluation</h4>
        <div v-for="r in schema.ratings" :key="r.key" class="apcwo-pr-rate-row">
          <span>{{ r.label }}</span>
          <rating-input :model-value="emp.ratings[r.key]" readonly />
        </div>
        <template v-for="q in schema.evaluationText" :key="q.key">
          <p class="apcwo-pr-modal__label">{{ q.label.toUpperCase() }}</p>
          <p class="apcwo-pr-modal__text">{{ emp[q.key] || '—' }}</p>
        </template>
      </template>
      <p v-else class="apcwo-pr-modal__muted">
        The employee has not submitted this review yet, so their answers are not
        visible.
      </p>

      <template v-if="review.hrData">
        <h4 class="apcwo-pr-modal__title">Final review</h4>
        <h4 class="apcwo-pr-modal__heading">Competency rating</h4>
        <div
          v-for="c in schema.competencies"
          :key="c.key"
          class="apcwo-pr-modal__hr"
        >
          <div class="apcwo-pr-rate-row">
            <strong>{{ c.label.toUpperCase() }}</strong>
            <rating-input
              :model-value="hr.competencies[c.key].rating"
              readonly
            />
          </div>
          <p v-if="hr.competencies[c.key].notes" class="apcwo-pr-modal__text">
            {{ hr.competencies[c.key].notes }}
          </p>
        </div>
        <h4 class="apcwo-pr-modal__heading">Overall employee performance</h4>
        <div
          v-for="c in schema.overall"
          :key="c.key"
          class="apcwo-pr-modal__hr"
        >
          <div class="apcwo-pr-rate-row">
            <strong>{{ c.label.toUpperCase() }}</strong>
            <rating-input :model-value="hr.overall[c.key].rating" readonly />
          </div>
          <p v-if="hr.overall[c.key].notes" class="apcwo-pr-modal__text">
            {{ hr.overall[c.key].notes }}
          </p>
        </div>
        <h4 class="apcwo-pr-modal__heading">EMPLOYEE POTENTIAL</h4>
        <p class="apcwo-pr-modal__text">{{ potentialLabel }}</p>
        <p v-if="hr.potentialComments" class="apcwo-pr-modal__text">
          {{ hr.potentialComments }}
        </p>
        <h4 class="apcwo-pr-modal__heading">Goals for the next year</h4>
        <p class="apcwo-pr-modal__text">{{ hr.goals || '—' }}</p>
        <h4 class="apcwo-pr-modal__heading">Overall comments</h4>
        <p class="apcwo-pr-modal__text">{{ hr.overallComments || '—' }}</p>
        <p class="apcwo-pr-modal__label">
          Reviewer: {{ hr.reviewerName || '—' }} · Review date:
          {{ hr.reviewDate || '—' }}
        </p>
      </template>

      <template v-if="review.logs && review.logs.length">
        <h4 class="apcwo-pr-modal__title">History</h4>
        <p v-for="(l, i) in review.logs" :key="i" class="apcwo-pr-modal__text">
          <strong>{{ l.action }}</strong> — {{ l.at }} ·
          {{ l.actor || 'unknown' }}<span v-if="l.note"> · {{ l.note }}</span>
        </p>
      </template>
    </div>

    <oxd-divider />
    <oxd-form-actions>
      <oxd-button
        type="button"
        display-type="ghost"
        label="Close"
        @click="onClose"
      />
      <oxd-button
        v-if="
          review &&
          (review.permissions.editHr || review.permissions.editManager)
        "
        type="button"
        display-type="secondary"
        label="Add Info"
        icon-name="pencil-fill"
        @click="openEdit"
      />
    </oxd-form-actions>
  </oxd-dialog>
</template>

<script>
import {OxdDialog} from '@ohrm/oxd';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import StatusBadge from './StatusBadge.vue';
import RatingInput from './RatingInput.vue';
import {API_BASE} from '../util/helpers';

export default {
  name: 'ReviewSummaryModal',
  components: {
    'status-badge': StatusBadge,
    'rating-input': RatingInput,
    'oxd-dialog': OxdDialog,
  },
  props: {
    reviewId: {
      type: Number,
      required: true,
    },
    employeeName: {
      type: String,
      default: '',
    },
  },
  emits: ['close'],
  data() {
    return {isLoading: true, review: null};
  },
  computed: {
    schema() {
      return this.review.schema;
    },
    emp() {
      return this.review.employeeData;
    },
    hr() {
      return this.review.hrData;
    },
    potentialLabel() {
      const match = this.schema.potential.find(
        (p) => p.key === this.hr.potential,
      );
      return match ? `☒ ${match.label.toUpperCase()}` : '—';
    },
    goalBlocks() {
      return [
        {
          key: 'spiritualGoals',
          title: this.schema.spiritual.title,
          rows: this.emp.spiritualGoals,
        },
        {
          key: 'professionalGoals',
          title: this.schema.professional.title,
          rows: this.emp.professionalGoals,
        },
      ];
    },
  },
  created() {
    new APIService(window.appGlobal.baseUrl, `${API_BASE}/reviews`)
      .get(this.reviewId)
      .then((response) => {
        this.review = response.data.data;
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    openEdit() {
      navigate(
        '/personalReview/viewPersonalReview/{id}',
        {id: this.reviewId},
        {tab: this.review.permissions.editHr ? 'hr' : 'mine'},
      );
    },
  },
};
</script>

<style>
.apcwo-pr-modal {
  width: 720px;
  max-width: 94vw;
}
.apcwo-pr-modal__body {
  max-height: 62vh;
  overflow-y: auto;
  padding-right: 4px;
}
.apcwo-pr-modal__facts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px 20px;
  margin-bottom: 8px;
  padding: 14px 16px;
  border: 1px solid #ebebf0;
  border-radius: 8px;
  background: #fafafc;
}
.apcwo-pr-modal__title {
  margin: 22px 0 0 0;
  padding-bottom: 6px;
  border-bottom: 2px solid #741e1e;
  font-size: 15px;
  font-weight: 700;
  color: #2b2b36;
  text-transform: uppercase;
}
.apcwo-pr-modal__label {
  margin: 10px 0 2px 0;
  font-size: 12px;
  font-weight: 700;
  color: #6b6b7a;
}
.apcwo-pr-modal__hr {
  margin-bottom: 6px;
}
.apcwo-pr-modal__heading {
  margin: 18px 0 6px 0;
  font-size: 14px;
  font-weight: 700;
  color: #741e1e;
  text-transform: uppercase;
}
.apcwo-pr-modal__text {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
}
.apcwo-pr-modal__goals {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
  line-height: 1.5;
}
.apcwo-pr-modal__muted {
  margin: 12px 0;
  font-size: 13px;
  color: #6b6b7a;
}
</style>
